import Link from 'next/link'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'accent'
type Size = 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-2 rounded font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50'

const variants: Record<Variant, string> = {
  // Primário: petróleo cheio, com leve elevação.
  primary: 'bg-brand text-white shadow-[0_6px_16px_rgb(14_92_99/0.20)] hover:bg-brand-dark',
  // Secundário: branco com borda hairline; foco/hover petróleo.
  secondary: 'border border-line bg-surface text-ink shadow-soft hover:border-brand hover:bg-brand-tint',
  // Acento quente (coral) — pra CTAs de marketing/energia.
  accent: 'bg-coral text-white shadow-[0_6px_16px_rgb(224_122_78/0.28)] hover:brightness-[0.96]',
}

const sizes: Record<Size, string> = {
  md: 'h-[46px] px-5 text-sm',
  lg: 'h-12 px-7 text-[15px]',
}

interface ButtonProps {
  variant?: Variant
  size?: Size
  /** Quando presente, renderiza um link (next/link) com cara de botão. */
  href?: string
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  fullWidth?: boolean
  className?: string
  children: React.ReactNode
}

/**
 * Botão do design system — cantos retos (radius 0 global), petróleo no primário.
 * Renderiza <a> (next/link) se receber `href`, senão <button>.
 */
export function Button({
  variant = 'primary',
  size = 'lg',
  href,
  onClick,
  type = 'button',
  disabled,
  fullWidth,
  className,
  children,
}: ButtonProps) {
  const cls = cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)

  if (href) {
    return (
      <Link href={href} className={cls} onClick={onClick}>
        {children}
      </Link>
    )
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={cls}>
      {children}
    </button>
  )
}
